/**
 * Dialog for editing an existing Reward Catalog Item.
 *
 * Pre-populated with existing values. Uses key={reward.name} in parent
 * to force remount when switching between rewards.
 *
 * Plan 177, Epic 07 — Admin Management & Analytics
 */

import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  useUpdateReward,
  type RewardCatalogItem,
} from "../api/rewardsAdminHooks";

interface EditRewardDialogProps {
  reward: RewardCatalogItem;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EditRewardDialog({
  reward,
  open,
  onOpenChange,
}: EditRewardDialogProps) {
  const [title, setTitle] = useState(reward.title);
  const [pointsCost, setPointsCost] = useState(reward.points_cost);
  const [description, setDescription] = useState(reward.description || "");

  const updateMutation = useUpdateReward();

  const handleSubmit = () => {
    if (!title.trim()) {
      toast.error("กรุณาตั้งชื่อรางวัล");
      return;
    }
    if (pointsCost <= 0) {
      toast.error("แต้มที่ใช้แลกต้องมากกว่า 0");
      return;
    }

    updateMutation.mutate(
      {
        reward_id: reward.name,
        title: title.trim(),
        points_cost: pointsCost,
        description: description.trim() || undefined,
      },
      {
        onSuccess: () => {
          toast.success("บันทึกรางวัลสำเร็จ");
          onOpenChange(false);
        },
        onError: (err) => {
          toast.error(err.message || "เกิดข้อผิดพลาด กรุณาลองอีกครั้ง");
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>แก้ไขรางวัล</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="edit-reward-title">ชื่อรางวัล</Label>
            <Input
              id="edit-reward-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          {/* Points cost */}
          <div className="space-y-2">
            <Label htmlFor="edit-reward-points">แต้มที่ใช้แลก</Label>
            <div className="flex items-center gap-2">
              <Input
                id="edit-reward-points"
                type="number"
                min={1}
                step={1}
                value={pointsCost}
                onChange={(e) => setPointsCost(Number(e.target.value))}
                className="w-32"
              />
              <span className="text-sm text-muted-foreground">แต้ม</span>
            </div>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="edit-reward-desc">
              รายละเอียด{" "}
              <span className="text-muted-foreground font-normal">
                (ไม่จำเป็น)
              </span>
            </Label>
            <Input
              id="edit-reward-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={updateMutation.isPending}
          >
            ยกเลิก
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={updateMutation.isPending}
            className="min-h-[44px]"
          >
            {updateMutation.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            บันทึก
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
